import { eventBus } from '../utils/EventEmitter.js';
import { createElement } from '../utils/dom.js';

/**
 * Manages preset images for quick access
 */
export class PresetManager {
    constructor(canvasManager) {
        this.canvasManager = canvasManager;
        this.defaultPresets = [
            { 
                name: 'Trump Snake',
                path: '/trump_snake.jpg'
            },
            { 
                name: 'Frog Car',
                path: '/rare-pepes/1734538915644-a-cartoon-of-a-frog-sitting-on-the-sidewalk-and-another-frog-in-a-car.gif'
            },
            { 
                name: 'Pepe Retro',
                path: '/rare-pepes/0515_-_atdB16V.jpg'
            },
            { 
                name: 'Pepe Pixel',
                path: '/rare-pepes/0673_-_zGNto6D.jpg'
            },
            { 
                name: 'Pepe Glitch',
                path: '/rare-pepes/0684_-_UPOmAnK.jpg'
            },
            { 
                name: 'Pepe Vaporwave',
                path: '/rare-pepes/0779_-_2HkXLmQ.png'
            },
            { 
                name: 'Pepe Classic',
                path: '/rare-pepes/0842_-_Yy81YRZ-200x200.png'
            },
            { 
                name: 'Pepe Mini',
                path: '/rare-pepes/0937_-_Y7tiQFK-150x150.jpg'
            },
            { 
                name: 'Pepe Art',
                path: '/rare-pepes/0975_-_YOUNFyz.jpg'
            }
        ];
        this.activePreset = null;
        this.setupPresets();

        // Listen for URL changes to update active preset
        eventBus.on('image:loadUrl', ({ url }) => {
            this.activePreset = url;
            this.updateActivePresetHighlight();
        });
    }

    /**
     * Get recent URLs from localStorage
     * @returns {Array} Array of preset objects from recent URLs
     */
    getRecentUrlPresets() {
        try {
            const urls = JSON.parse(localStorage.getItem('recentUrls')) || [];
            return urls.map(url => {
                // Try to extract a name from the URL
                let name;
                try {
                    const urlObj = new URL(url);
                    const filename = urlObj.pathname.split('/').pop();
                    name = filename || urlObj.hostname;
                    // Clean up the name
                    name = decodeURIComponent(name)
                        .replace(/[_-]/g, ' ')
                        .replace(/\.\w+$/, '') // Remove file extension
                        .split(' ')
                        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
                        .join(' ');
                } catch {
                    name = 'Custom Image';
                }
                return {
                    name: name,
                    path: url,
                    isCustom: true
                };
            });
        } catch {
            return [];
        }
    }

    /**
     * Set up presets gallery
     */
    setupPresets() {
        // Get recent URLs and combine with default presets
        const recentPresets = this.getRecentUrlPresets();
        this.presets = [...recentPresets, ...this.defaultPresets];

        // Create presets gallery
        const gallery = createElement('div', { className: 'presets-gallery' });
        
        // Add title
        const title = createElement('h3', {
            textContent: 'Presets',
            style: {
                margin: '0 0 10px 0',
                fontSize: '1rem',
                opacity: '0.8'
            }
        });
        gallery.appendChild(title);

        // Create thumbnails container
        const thumbnails = createElement('div', { className: 'preset-thumbnails' });

        // Add thumbnails for all presets
        this.presets.forEach((preset) => {
            const thumbnail = this.createPresetThumbnail(preset);
            if (preset.isCustom) {
                thumbnail.classList.add('custom-preset');
                // Add a subtle indicator that this is a custom preset
                const indicator = createElement('div', {
                    className: 'custom-indicator',
                    textContent: '★',
                    style: {
                        position: 'absolute',
                        top: '5px',
                        right: '5px',
                        color: 'var(--accent-color)',
                        fontSize: '12px',
                        opacity: '0.8'
                    }
                });
                thumbnail.appendChild(indicator);
            }
            thumbnails.appendChild(thumbnail);
        });

        gallery.appendChild(thumbnails);

        // Add to controls panel
        const controlsPanel = document.getElementById('controlsPanel');
        const uploadSection = controlsPanel.querySelector('.upload-section');
        controlsPanel.insertBefore(gallery, uploadSection.nextSibling);
    }

    /**
     * Update presets list with latest URLs
     */
    updatePresets() {
        // Get fresh list of presets
        const recentPresets = this.getRecentUrlPresets();
        this.presets = [...recentPresets, ...this.defaultPresets];

        // Update thumbnails
        const thumbnailsContainer = document.querySelector('.preset-thumbnails');
        thumbnailsContainer.innerHTML = '';

        this.presets.forEach((preset) => {
            const thumbnail = this.createPresetThumbnail(preset);
            if (preset.isCustom) {
                thumbnail.classList.add('custom-preset');
                const indicator = createElement('div', {
                    className: 'custom-indicator',
                    textContent: '★',
                    style: {
                        position: 'absolute',
                        top: '5px',
                        right: '5px',
                        color: 'var(--accent-color)',
                        fontSize: '12px',
                        opacity: '0.8'
                    }
                });
                thumbnail.appendChild(indicator);
            }
            thumbnailsContainer.appendChild(thumbnail);
        });

        // Update active highlight
        this.updateActivePresetHighlight();
    }

    /**
     * Create a preset thumbnail
     * @param {Object} preset - The preset configuration
     * @returns {HTMLElement} The thumbnail element
     */
    createPresetThumbnail(preset) {
        const thumbnail = createElement('div', {
            className: `preset-thumbnail ${preset.path === this.activePreset ? 'active' : ''}`,
            onClick: () => this.loadPresetImage(preset.path)
        });

        const img = createElement('img', {
            src: preset.path,
            alt: preset.name,
            title: preset.name,
            onerror: (e) => {
                e.target.src = '/trump_snake.jpg';
            }
        });

        thumbnail.appendChild(img);
        return thumbnail;
    }

    /**
     * Load a preset image
     * @param {string} path - The preset path
     */
    loadPresetImage(path) {
        this.activePreset = path;
        
        // Store preset path as last URL
        localStorage.setItem('lastUrl', path);
        
        // Track preset usage
        if (typeof gtag !== 'undefined') {
            gtag('event', 'use_preset', {
                'event_category': 'Preset',
                'event_label': path
            });
        }
        
        // Check if preset is a GIF
        if (path.toLowerCase().endsWith('.gif')) {
            eventBus.emit('image:loadGif', { url: path, shouldRevokeUrl: false });
        } else {
            // Load static image
            const img = new Image();
            img.crossOrigin = 'anonymous';
            img.onload = () => {
                eventBus.emit('image:loaded', img);
            };
            img.onerror = () => {
                console.error('Failed to load preset image:', path);
                // Load default image as fallback
                const fallbackImg = new Image();
                fallbackImg.crossOrigin = 'anonymous';
                fallbackImg.onload = () => {
                    eventBus.emit('image:loaded', fallbackImg);
                };
                fallbackImg.src = '/trump_snake.jpg';
            };
            img.src = path;
        }

        // Update active highlight
        this.updateActivePresetHighlight();

        // Close sidebar on mobile
        if (window.innerWidth <= 768) {
            const controlsPanel = document.getElementById('controlsPanel');
            controlsPanel.classList.remove('show');
        }
    }

    updateActivePresetHighlight() {
        // Remove active class from all thumbnails
        const thumbnails = document.querySelectorAll('.preset-thumbnail');
        thumbnails.forEach(thumb => thumb.classList.remove('active'));

        // Add active class to current preset
        if (this.activePreset) {
            const activeThumbnail = Array.from(thumbnails)
                .find(thumb => thumb.querySelector('img').src.endsWith(this.activePreset));
            if (activeThumbnail) {
                activeThumbnail.classList.add('active');
            }
        }
    }
} 